export const CHALLENGE_TYPE_LABELS = {
  chase_target: 'Chase target',
  hit_sixes: 'Hit sixes',
  consecutive_sixes: 'Consecutive sixes',
  score_in_balls: 'Score in balls',
  take_wickets: 'Take wickets',
  defend_runs: 'Defend runs',
  no_boundaries: 'No boundaries',
  no_dot_balls: 'No dot balls',
  no_wicket_survival: 'No wicket survival',
};

/** IST calendar day as YYYY-MM-DD */
export function getIstDateString(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function addDaysToDateString(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d + days));
  return utc.toISOString().slice(0, 10);
}

export function getDefaultSuccessConfigFields(challengeType) {
  switch (challengeType) {
    case 'chase_target':
      return { target_runs: '20', max_balls: '' };
    case 'hit_sixes':
      return { sixes: '3' };
    case 'consecutive_sixes':
      return { consecutive: '2' };
    case 'score_in_balls':
      return { runs: '15', balls: '6' };
    case 'take_wickets':
      return { wickets: '2', max_balls: '' };
    case 'defend_runs':
      return { max_conceded: '15', max_balls: '6' };
    case 'no_boundaries':
      return { role: 'bowling' };
    case 'no_dot_balls':
      return { balls: '6' };
    case 'no_wicket_survival':
      return { max_balls: '6' };
    default:
      return {};
  }
}

export function successConfigFieldsFromChallenge(challenge) {
  const config = challenge?.success_config || {};
  const type = challenge?.challenge_type;
  const merged = { ...getDefaultSuccessConfigFields(type) };

  Object.entries(config).forEach(([key, value]) => {
    if (value != null && value !== '') {
      merged[key] = String(value);
    }
  });

  return merged;
}

function parseOptionalInt(value) {
  if (value === '' || value == null) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

function parseRequiredInt(value, label) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return { error: `${label} must be a number ≥ 0` };
  }
  return { value: n };
}

function parseRequiredPositiveInt(value, label) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) {
    return { error: `${label} must be a number ≥ 1` };
  }
  return { value: n };
}

/** Build API success_config from form field strings. Returns { config } or { error }. */
export function buildSuccessConfig(challengeType, fields) {
  switch (challengeType) {
    case 'chase_target': {
      const target = parseRequiredInt(fields.target_runs, 'Target runs');
      if (target.error) return target;
      const config = { target_runs: target.value };
      const maxBalls = parseOptionalInt(fields.max_balls);
      if (Number.isNaN(maxBalls)) return { error: 'Max balls must be a valid number' };
      if (maxBalls != null) config.max_balls = maxBalls;
      return { config };
    }
    case 'hit_sixes': {
      const sixes = parseRequiredInt(fields.sixes, 'Sixes');
      if (sixes.error) return sixes;
      return { config: { sixes: sixes.value } };
    }
    case 'consecutive_sixes': {
      const consecutive = parseRequiredInt(fields.consecutive, 'Consecutive sixes');
      if (consecutive.error) return consecutive;
      return { config: { consecutive: consecutive.value } };
    }
    case 'score_in_balls': {
      const runs = parseRequiredInt(fields.runs, 'Runs');
      if (runs.error) return runs;
      const balls = parseRequiredInt(fields.balls, 'Balls');
      if (balls.error) return balls;
      return { config: { runs: runs.value, balls: balls.value } };
    }
    case 'take_wickets': {
      const wickets = parseRequiredInt(fields.wickets, 'Wickets');
      if (wickets.error) return wickets;
      const config = { wickets: wickets.value };
      const maxBalls = parseOptionalInt(fields.max_balls);
      if (Number.isNaN(maxBalls)) return { error: 'Max balls must be a valid number' };
      if (maxBalls != null) config.max_balls = maxBalls;
      return { config };
    }
    case 'defend_runs': {
      const maxConceded = parseRequiredInt(fields.max_conceded, 'Max conceded');
      if (maxConceded.error) return maxConceded;
      const config = { max_conceded: maxConceded.value };
      const maxBalls = parseOptionalInt(fields.max_balls);
      if (Number.isNaN(maxBalls)) return { error: 'Max balls must be a valid number' };
      if (maxBalls != null) config.max_balls = maxBalls;
      return { config };
    }
    case 'no_boundaries': {
      const role = fields.role;
      if (role !== 'bowling' && role !== 'batting') {
        return { error: 'Role must be bowling or batting' };
      }
      return { config: { role } };
    }
    case 'no_dot_balls': {
      const balls = parseRequiredPositiveInt(fields.balls, 'Balls');
      if (balls.error) return balls;
      return { config: { balls: balls.value } };
    }
    case 'no_wicket_survival': {
      const maxBalls = parseRequiredPositiveInt(fields.max_balls, 'Max balls');
      if (maxBalls.error) return maxBalls;
      return { config: { max_balls: maxBalls.value } };
    }
    default:
      return { error: 'Invalid challenge type' };
  }
}

export function formatSuccessConfigSummary(challengeType, config = {}) {
  switch (challengeType) {
    case 'chase_target':
      return config.max_balls != null
        ? `≥ ${config.target_runs} runs in ≤ ${config.max_balls} balls`
        : `≥ ${config.target_runs} runs`;
    case 'hit_sixes':
      return `≥ ${config.sixes} sixes`;
    case 'consecutive_sixes':
      return `≥ ${config.consecutive} sixes in a row`;
    case 'score_in_balls':
      return `≥ ${config.runs} runs in ≤ ${config.balls} balls`;
    case 'take_wickets':
      return config.max_balls != null
        ? `≥ ${config.wickets} wickets in ≤ ${config.max_balls} balls`
        : `≥ ${config.wickets} wickets`;
    case 'defend_runs':
      return config.max_balls != null
        ? `≤ ${config.max_conceded} runs in ${config.max_balls} balls`
        : `≤ ${config.max_conceded} runs conceded`;
    case 'no_boundaries':
      return config.role === 'batting' ? 'No boundaries hit' : 'No boundaries conceded';
    case 'no_dot_balls':
      return `No dot balls in ${config.balls} balls faced (every ball ≥ 1 run)`;
    case 'no_wicket_survival':
      return `No wicket lost in ${config.max_balls} balls faced`;
    default:
      return JSON.stringify(config);
  }
}

export function challengeToForm(challenge) {
  let metadataJson = '';
  if (challenge?.metadata && Object.keys(challenge.metadata).length > 0) {
    try {
      metadataJson = JSON.stringify(challenge.metadata, null, 2);
    } catch {
      metadataJson = '';
    }
  }

  return {
    title: challenge?.title || '',
    objective: challenge?.objective || '',
    challenge_type: challenge?.challenge_type || 'hit_sixes',
    active_date: challenge?.active_date || getIstDateString(),
    reward_fe: String(challenge?.reward_fe ?? 25),
    energy_cost: String(challenge?.energy_cost ?? 0),
    enabled: challenge?.enabled !== false,
    image_url: challenge?.image_url || '',
    cta_text: challenge?.cta_text || 'Play',
    sort_order: String(challenge?.sort_order ?? 0),
    countdown_seconds: String(challenge?.countdown_seconds ?? 15),
    badge: challenge?.badge || '',
    how_to_play: challenge?.how_to_play || '',
    objectives: challenge?.objectives || '',
    objectives_preview: challenge?.objectives_preview || '',
    metadata_json: metadataJson,
    success_config_fields: successConfigFieldsFromChallenge(challenge),
  };
}

export function emptyChallengeForm() {
  return challengeToForm({
    challenge_type: 'hit_sixes',
    active_date: getIstDateString(),
    reward_fe: 25,
    energy_cost: 0,
    enabled: true,
    cta_text: 'Play',
    sort_order: 0,
    countdown_seconds: 15,
    success_config: getDefaultSuccessConfigFields('hit_sixes'),
  });
}

export function buildChallengePayload(form, { includeAllFields = true } = {}) {
  if (!form.title?.trim()) return { error: 'Title is required' };
  if (!form.objective?.trim()) return { error: 'Objective is required' };
  if (!form.active_date) return { error: 'Active date is required' };

  const builtConfig = buildSuccessConfig(form.challenge_type, form.success_config_fields || {});
  if (builtConfig.error) return builtConfig;

  const rewardFe = Number(form.reward_fe);
  if (!Number.isFinite(rewardFe) || rewardFe < 0) {
    return { error: 'Reward FE must be a number ≥ 0' };
  }

  const energyCost = Number(form.energy_cost);
  if (!Number.isFinite(energyCost) || energyCost < 0) {
    return { error: 'Energy cost must be a number ≥ 0' };
  }

  const countdownSeconds = Number(form.countdown_seconds);
  if (!Number.isFinite(countdownSeconds) || countdownSeconds < 1 || countdownSeconds > 120) {
    return { error: 'Countdown seconds must be between 1 and 120' };
  }

  let metadata;
  if (form.metadata_json?.trim()) {
    try {
      metadata = JSON.parse(form.metadata_json);
      if (typeof metadata !== 'object' || metadata === null || Array.isArray(metadata)) {
        return { error: 'Metadata must be a JSON object' };
      }
    } catch {
      return { error: 'Metadata must be valid JSON' };
    }
  }

  const objectivesTrimmed = form.objectives?.trim();

  const payload = {
    title: form.title.trim(),
    objective: form.objective.trim(),
    challenge_type: form.challenge_type,
    success_config: builtConfig.config,
    active_date: form.active_date,
    reward_fe: rewardFe,
    energy_cost: energyCost,
    enabled: form.enabled,
    cta_text: form.cta_text?.trim() || 'Play',
    image_url: form.image_url?.trim() || null,
    sort_order: Number(form.sort_order) || 0,
    countdown_seconds: countdownSeconds,
    badge: form.badge?.trim() || null,
    how_to_play: form.how_to_play?.trim() || null,
    objectives: objectivesTrimmed || null,
  };

  if (metadata !== undefined) {
    payload.metadata = metadata;
  } else if (includeAllFields) {
    payload.metadata = {};
  }

  return { payload };
}

export function countEnabledForDate(rows, activeDate, excludeId) {
  if (!activeDate || !Array.isArray(rows)) return 0;
  return rows.filter(
    (row) =>
      row.enabled &&
      row.active_date === activeDate &&
      String(row.id) !== String(excludeId ?? '')
  ).length;
}

export function enabledCountByDate(rows) {
  const counts = {};
  if (!Array.isArray(rows)) return counts;
  rows.forEach((row) => {
    if (row.enabled && row.active_date) {
      counts[row.active_date] = (counts[row.active_date] || 0) + 1;
    }
  });
  return counts;
}

/** Markdown preview text: custom objectives override, else server preview. */
export function resolveObjectivesPreview(form) {
  if (form.objectives?.trim()) {
    return form.objectives.trim();
  }
  return form.objectives_preview?.trim() || '';
}
